/**
 * What the share card of each public page says, and where its image lives.
 *
 * One card per page and language, drawn at build time by `scripts/og/render.ts`
 * from the entries below — the page's own constants (`LANDING_NUMBERS`, the
 * thresholds in `shared/structure.ts`, the plans, the sittings), never typed
 * figures, so a number that moves on the page moves on the picture with the
 * next build. `PageMeta` reads the same entry for the tags and the alt text.
 *
 * Titles are shortened on purpose: a card is read at thumbnail size in a
 * messenger, and three lines is the most it takes. Every figure in a `sub` or a
 * chip is a fact of the page it stands for.
 *
 * `OG_VERSION` is in the path. Telegram and Facebook cache a picture by URL for
 * weeks; changing the design means changing the version, and the old files are
 * simply never linked again.
 */
import { LANDING_NUMBERS as N } from "@/features/landing/numbers";
import { sittingYears } from "@shared/certyfikat-sittings";
import { PASS } from "@shared/plans";
import { TEST_MAX, TEST_THRESHOLDS, WRITTEN_MINUTES } from "@shared/structure";
import { WORD_SETS } from "@shared/vocabulary";
import { POSTS } from "@/features/blog/catalogue";

export const OG_VERSION = "v2";
export const OG_WIDTH = 1200;
export const OG_HEIGHT = 630;

export type OgLocale = "pl" | "uk" | "ru" | "en";
export const OG_LOCALES: OgLocale[] = ["pl", "uk", "ru", "en"];

/** The picture on the right of a card. `none` gives the title the whole width. */
export type OgPreview =
  | { kind: "none" }
  | { kind: "telc-result" }
  | { kind: "telc-thresholds" }
  | { kind: "telc-parts" }
  | { kind: "state-result"; bar: 50 | 60 }
  | { kind: "compare" }
  | { kind: "badge"; level: "B1" | "B2"; accepts: "state" | "both" }
  | { kind: "sittings" }
  | { kind: "pricing" }
  | { kind: "papers" }
  | { kind: "writing" }
  | { kind: "telc-oral"; part: 1 | 2 }
  | { kind: "state-oral"; level: "B1" | "B2" }
  | { kind: "state-writing" }
  | { kind: "words" };

export interface OgCard {
  title: string;
  sub: string;
  chips: string[];
  preview: OgPreview;
}

interface Entry {
  preview: OgPreview;
  pl: () => Omit<OgCard, "preview">;
  uk: () => Omit<OgCard, "preview">;
  ru: () => Omit<OgCard, "preview">;
  en: () => Omit<OgCard, "preview">;
}

const b1 = TEST_THRESHOLDS.find((t) => t.level === "B1")!.min;
const b2 = TEST_THRESHOLDS.find((t) => t.level === "B2")!.min;
const price = `${PASS.amountMinor / 100} zł`;
const today = () => new Date().toISOString().slice(0, 10);
const c = (title: string, sub: string, chips: string[] = []) => ({ title, sub, chips });

const ENTRIES: Record<string, Entry> = {
  home: {
    preview: { kind: "telc-result" },
    pl: () =>
      c("Przygotuj się do egzaminu z polskiego B1·B2", "telc i egzamin państwowy: cała część pisemna na czas", [
        `${N.fullSets} zestawów w formacie telc`,
        `${N.certyfikatPapers} arkuszy z sesji`,
        `${price} / ${PASS.days} dni`,
      ]),
    uk: () =>
      c("Підготуйся до іспиту з польської B1·B2", "telc і державний іспит: уся письмова частина на час", [
        `${N.fullSets} наборів у форматі telc`,
        `${N.certyfikatPapers} наборів із сесій`,
        `${price} / ${PASS.days} днів`,
      ]),
    ru: () =>
      c("Подготовься к экзамену по польскому B1·B2", "telc и государственный экзамен: вся письменная часть на время", [
        `${N.fullSets} наборов в формате telc`,
        `${N.certyfikatPapers} наборов с сессий`,
        `${price} / ${PASS.days} дней`,
      ]),
    en: () =>
      c("Prepare for the Polish exam, B1·B2", "telc and the state exam: the whole written paper, timed", [
        `${N.fullSets} sets in the telc format`,
        `${N.certyfikatPapers} papers from sittings`,
        `${price} / ${PASS.days} days`,
      ]),
  },
  telc: {
    preview: { kind: "telc-thresholds" },
    pl: () => c("Egzamin telc z polskiego B1·B2", `Cała część pisemna pod zegarem: ${WRITTEN_MINUTES} minut`, [`${N.fullSets} zestawów`, "wynik po 4–6 tygodniach"]),
    uk: () => c("Іспит telc з польської B1·B2", `Уся письмова частина на час: ${WRITTEN_MINUTES} хвилин`, [`${N.fullSets} наборів`, "результат за 4–6 тижнів"]),
    ru: () => c("Экзамен telc по польскому B1·B2", `Вся письменная часть на время: ${WRITTEN_MINUTES} минут`, [`${N.fullSets} наборов`, "результат через 4–6 недель"]),
    en: () => c("The telc Polish exam, B1·B2", `The whole written paper against the clock: ${WRITTEN_MINUTES} minutes`, [`${N.fullSets} sets`, "result in 4–6 weeks"]),
  },
  "telc-zasady": {
    preview: { kind: "telc-thresholds" },
    pl: () => c("Progi telc B1·B2: ile punktów trzeba zdobyć", `B1 od ${b1}, B2 od ${b2} z ${TEST_MAX} punktów`, [`${WRITTEN_MINUTES} minut`, "wynik po 4–6 tygodniach"]),
    uk: () => c("Пороги telc B1·B2: скільки балів потрібно", `B1 від ${b1}, B2 від ${b2} з ${TEST_MAX} балів`, [`${WRITTEN_MINUTES} хвилин`, "результат за 4–6 тижнів"]),
    ru: () => c("Пороги telc B1·B2: сколько баллов нужно", `B1 от ${b1}, B2 от ${b2} из ${TEST_MAX} баллов`, [`${WRITTEN_MINUTES} минут`, "результат через 4–6 недель"]),
    en: () => c("telc B1·B2 pass marks: how many points you need", `B1 from ${b1}, B2 from ${b2} out of ${TEST_MAX} points`, [`${WRITTEN_MINUTES} minutes`, "result in 4–6 weeks"]),
  },
  "telc-zadania": {
    preview: { kind: "telc-parts" },
    pl: () => c("Zadania telc B1·B2: co jest w każdej części", "Słuchanie, czytanie, elementy języka, pisanie", [`${TEST_MAX} zadań zamkniętych`, `${WRITTEN_MINUTES} minut`]),
    uk: () => c("Завдання telc B1·B2: що в кожній частині", "Слухання, читання, елементи мови, письмо", [`${TEST_MAX} закритих завдань`, `${WRITTEN_MINUTES} хвилин`]),
    ru: () => c("Задания telc B1·B2: что в каждой части", "Аудирование, чтение, элементы языка, письмо", [`${TEST_MAX} закрытых заданий`, `${WRITTEN_MINUTES} минут`]),
    en: () => c("telc B1·B2 tasks: what each part asks", "Listening, reading, language elements, writing", [`${TEST_MAX} closed items`, `${WRITTEN_MINUTES} minutes`]),
  },
  certyfikat: {
    preview: { kind: "state-result", bar: 50 },
    pl: () => c("Państwowy egzamin z polskiego B1·B2", "Prawdziwe arkusze z sesji 2021–2024", [`${N.certyfikatPapers} arkuszy z sesji`, "50% z każdej części", "liczy się na obywatelstwo"]),
    uk: () => c("Державний іспит з польської B1·B2", "Справжні набори із сесій 2021–2024", [`${N.certyfikatPapers} наборів із сесій`, "50% з кожної частини", "потрібен на громадянство"]),
    ru: () => c("Государственный экзамен по польскому B1·B2", "Настоящие наборы с сессий 2021–2024", [`${N.certyfikatPapers} наборов с сессий`, "50% в каждой части", "нужен на гражданство"]),
    en: () => c("The Polish state exam, B1·B2", "Real papers from the 2021–2024 sittings", [`${N.certyfikatPapers} papers from sittings`, "50% in every part", "counts for citizenship"]),
  },
  "certyfikat-b1": {
    preview: { kind: "state-result", bar: 50 },
    pl: () => c("Państwowy egzamin z polskiego B1", "Pięć części, pisemna do 190 minut", [`${N.certyfikatB1} arkuszy z sesji`, "50% z każdej części"]),
    uk: () => c("Державний іспит з польської B1", "П’ять частин, письмова до 190 хвилин", [`${N.certyfikatB1} наборів із сесій`, "50% з кожної частини"]),
    ru: () => c("Государственный экзамен по польскому B1", "Пять частей, письменная до 190 минут", [`${N.certyfikatB1} наборов с сессий`, "50% в каждой части"]),
    en: () => c("The Polish state exam, B1", "Five parts, the written paper up to 190 minutes", [`${N.certyfikatB1} papers from sittings`, "50% in every part"]),
  },
  "certyfikat-b2": {
    preview: { kind: "state-result", bar: 60 },
    pl: () => c("Państwowy egzamin z polskiego B2", "Pięć części, pisemna do 250 minut", [`${N.certyfikatB2} arkuszy z sesji`, "60% z każdej części"]),
    uk: () => c("Державний іспит з польської B2", "П’ять частин, письмова до 250 хвилин", [`${N.certyfikatB2} наборів із сесій`, "60% з кожної частини"]),
    ru: () => c("Государственный экзамен по польскому B2", "Пять частей, письменная до 250 минут", [`${N.certyfikatB2} наборов с сессий`, "60% в каждой части"]),
    en: () => c("The Polish state exam, B2", "Five parts, the written paper up to 250 minutes", [`${N.certyfikatB2} papers from sittings`, "60% in every part"]),
  },
  "certyfikat-zasady": {
    preview: { kind: "state-result", bar: 50 },
    pl: () => c("Progi państwowego egzaminu z polskiego", "50% z każdej części na B1, 60% na B2", ["jedna część poniżej progu = cała sesja"]),
    uk: () => c("Пороги державного іспиту з польської", "50% з кожної частини на B1, 60% на B2", ["одна частина нижче порога — уся сесія"]),
    ru: () => c("Пороги государственного экзамена по польскому", "50% в каждой части на B1, 60% на B2", ["одна часть ниже порога — вся сессия"]),
    en: () => c("Pass marks of the Polish state exam", "50% in every part at B1, 60% at B2", ["one part below the bar fails the sitting"]),
  },
  "certyfikat-zadania": {
    preview: { kind: "state-result", bar: 50 },
    pl: () => c("Zadania państwowego egzaminu z polskiego", "Co trzeba zrobić w każdej części, B1 i B2", [`${N.certyfikatPapers} arkuszy z sesji`]),
    uk: () => c("Завдання державного іспиту з польської", "Що треба зробити в кожній частині, B1 і B2", [`${N.certyfikatPapers} наборів із сесій`]),
    ru: () => c("Задания государственного экзамена по польскому", "Что нужно сделать в каждой части, B1 и B2", [`${N.certyfikatPapers} наборов с сессий`]),
    en: () => c("Tasks of the Polish state exam", "What you do in each part, B1 and B2", [`${N.certyfikatPapers} papers from sittings`]),
  },
  "telc-list": {
    preview: { kind: "writing" },
    pl: () => c("E-mail na telc z polskiego B1: schemat", "Trzy punkty, ton, zwroty i przykład", ["30 minut na pisanie"]),
    uk: () => c("E-mail на telc з польської B1: схема", "Три пункти, тон, звертання й приклад", ["30 хвилин на письмо"]),
    ru: () => c("E-mail на telc по польскому B1: схема", "Три пункта, тон, обращение и пример", ["30 минут на письмо"]),
    en: () => c("The telc Polish B1 email: a structure", "Three points, register, phrases and an example", ["30 minutes for writing"]),
  },
  "telc-opis-zdjecia": {
    preview: { kind: "telc-oral", part: 1 },
    pl: () => c("Opis zdjęcia na telc z polskiego B1·B2", "Plan wypowiedzi i pełny przykład", ["część 1 ustnego telc"]),
    uk: () => c("Опис фото на telc з польської B1·B2", "План відповіді й повний приклад", ["частина 1 усного telc"]),
    ru: () => c("Описание фото на telc по польскому B1·B2", "План ответа и полный пример", ["часть 1 устного telc"]),
    en: () => c("Describing a picture, telc Polish B1·B2", "A speaking plan and a full example", ["part 1 of the telc oral"]),
  },
  "telc-prezentacja": {
    preview: { kind: "telc-oral", part: 2 },
    pl: () => c("Prezentacja na telc z polskiego B1·B2", "Struktura, zwroty i pełny przykład", ["część 2 ustnego telc"]),
    uk: () => c("Презентація на telc з польської B1·B2", "Структура, фрази й повний приклад", ["частина 2 усного telc"]),
    ru: () => c("Презентация на telc по польскому B1·B2", "Структура, фразы и полный пример", ["часть 2 устного telc"]),
    en: () => c("The telc Polish B1·B2 presentation", "Structure, phrases and a full example", ["part 2 of the telc oral"]),
  },
  "certyfikat-pisanie": {
    preview: { kind: "state-writing" },
    pl: () => c("Pisanie na egzaminie państwowym B1 i B2", "Dwa teksty z jednego zestawu, z przykładami", ["krótki i długi tekst"]),
    uk: () => c("Письмо на державному іспиті B1 і B2", "Два тексти з одного набору, зі зразками", ["короткий і довгий текст"]),
    ru: () => c("Письмо на государственном экзамене B1 и B2", "Два текста из одного набора, с образцами", ["короткий и длинный текст"]),
    en: () => c("Writing in the Polish state exam, B1 and B2", "Two texts from one set, with examples", ["a short and a long text"]),
  },
  "certyfikat-mowienie-b1": {
    preview: { kind: "state-oral", level: "B1" },
    pl: () => c("Mówienie na egzaminie państwowym B1", "Zdjęcie, monolog i rozmowa z przykładami", ["do 15 minut"]),
    uk: () => c("Говоріння на державному іспиті B1", "Фото, монолог і розмова зі зразками", ["до 15 хвилин"]),
    ru: () => c("Говорение на государственном экзамене B1", "Фото, монолог и разговор с образцами", ["до 15 минут"]),
    en: () => c("Speaking in the Polish state exam, B1", "Picture, monologue and conversation, with examples", ["up to 15 minutes"]),
  },
  "certyfikat-mowienie-b2": {
    preview: { kind: "state-oral", level: "B2" },
    pl: () => c("Mówienie na egzaminie państwowym B2", "Zdjęcia, tekst i monolog z przykładami", ["do 15 minut"]),
    uk: () => c("Говоріння на державному іспиті B2", "Фото, текст і монолог зі зразками", ["до 15 хвилин"]),
    ru: () => c("Говорение на государственном экзамене B2", "Фото, текст и монолог с образцами", ["до 15 минут"]),
    en: () => c("Speaking in the Polish state exam, B2", "Photos, a text and a monologue, with examples", ["up to 15 minutes"]),
  },
  porownanie: {
    preview: { kind: "compare" },
    pl: () => c("telc czy egzamin państwowy z polskiego?", "Kto przyjmuje, jaki próg, kiedy wynik", ["obywatelstwo: tylko państwowy", "karta pobytu: oba"]),
    uk: () => c("telc чи державний іспит з польської?", "Хто приймає, який поріг, коли результат", ["громадянство: лише державний", "карта резидента: обидва"]),
    ru: () => c("telc или государственный экзамен по польскому?", "Кто принимает, какой порог, когда результат", ["гражданство: только государственный", "карта резидента: оба"]),
    en: () => c("telc or the state exam: which Polish exam?", "Who accepts it, the pass mark, when the result comes", ["citizenship: state exam only", "residence card: both"]),
  },
  obywatelstwo: {
    preview: { kind: "badge", level: "B1", accepts: "state" },
    pl: () => c("Polski na obywatelstwo: certyfikat B1", "Liczy się tylko państwowy egzamin certyfikatowy", ["50% z każdej z pięciu części"]),
    uk: () => c("Польська на громадянство: сертифікат B1", "Приймають лише державний сертифікатний іспит", ["50% з кожної з п’яти частин"]),
    ru: () => c("Польский на гражданство: сертификат B1", "Принимают только государственный сертификатный экзамен", ["50% в каждой из пяти частей"]),
    en: () => c("Polish for citizenship: the B1 certificate", "Only the state certificate exam is accepted", ["50% in each of the five parts"]),
  },
  "karta-pobytu": {
    preview: { kind: "badge", level: "B1", accepts: "both" },
    pl: () => c("Polski na kartę rezydenta UE: certyfikat B1", "Liczą się oba egzaminy: telc i państwowy", ["od poziomu B1"]),
    uk: () => c("Польська на карту резидента ЄС: сертифікат B1", "Приймають обидва іспити: telc і державний", ["від рівня B1"]),
    ru: () => c("Польский на карту резидента ЕС: сертификат B1", "Принимают оба экзамена: telc и государственный", ["от уровня B1"]),
    en: () => c("Polish for the EU residence card: a B1 certificate", "Both exams count: telc and the state exam", ["from level B1"]),
  },
  studia: {
    preview: { kind: "badge", level: "B2", accepts: "both" },
    pl: () => c("Polski na studia: certyfikat B2", "Liczą się oba egzaminy, zwykle od B2", [`telc ${WRITTEN_MINUTES} min`, "państwowy do 250 min"]),
    uk: () => c("Польська на навчання: сертифікат B2", "Приймають обидва іспити, зазвичай від B2", [`telc ${WRITTEN_MINUTES} хв`, "державний до 250 хв"]),
    ru: () => c("Польский для учёбы: сертификат B2", "Принимают оба экзамена, обычно от B2", [`telc ${WRITTEN_MINUTES} мин`, "государственный до 250 мин"]),
    en: () => c("Polish for university: a B2 certificate", "Both exams count, usually from B2", [`telc ${WRITTEN_MINUTES} min`, "state exam up to 250 min"]),
  },
  terminy: {
    preview: { kind: "sittings" },
    pl: () => c(`Terminy egzaminu z polskiego ${sittingYears(today())}`, "Sesje państwowego B1·B2 i czekanie na certyfikat", ["sprawdzanie 3–4 miesiące", "+ do 30 dni na certyfikat"]),
    uk: () => c(`Дати іспиту з польської ${sittingYears(today())}`, "Сесії державного B1·B2 і очікування сертифіката", ["перевірка 3–4 місяці", "+ до 30 днів на сертифікат"]),
    ru: () => c(`Даты экзамена по польскому ${sittingYears(today())}`, "Сессии государственного B1·B2 и ожидание сертификата", ["проверка 3–4 месяца", "+ до 30 дней на сертификат"]),
    en: () => c(`Polish exam dates ${sittingYears(today())}`, "State exam sittings, B1·B2, and the wait for the certificate", ["marking 3–4 months", "+ up to 30 days for the certificate"]),
  },
  pricing: {
    preview: { kind: "pricing" },
    pl: () => c("Cennik: jeden dostęp do obu egzaminów", `${N.freeSets} zestawy za darmo, bez karty`, ["BLIK lub karta", "zwrot w 14 dni"]),
    uk: () => c("Ціни: один доступ до обох іспитів", `${N.freeSets} набори безкоштовно, без картки`, ["BLIK або картка", "повернення за 14 днів"]),
    ru: () => c("Цены: один доступ к обоим экзаменам", `${N.freeSets} набора бесплатно, без карты`, ["BLIK или карта", "возврат за 14 дней"]),
    en: () => c("Pricing: one access to both exams", `${N.freeSets} sets free, no card`, ["BLIK or card", "14-day refund"]),
  },
  arkusze: {
    preview: { kind: "papers" },
    pl: () => c("Oficjalne arkusze z polskiego: skąd je pobrać", "Arkusze Komisji z sesji 2021–2024", [`${N.certyfikatB1} na B1`, `${N.certyfikatB2} na B2`]),
    uk: () => c("Офіційні аркуші з польської: де їх узяти", "Набори Комісії із сесій 2021–2024", [`${N.certyfikatB1} на B1`, `${N.certyfikatB2} на B2`]),
    ru: () => c("Официальные материалы по польскому: где их взять", "Наборы Комиссии с сессий 2021–2024", [`${N.certyfikatB1} на B1`, `${N.certyfikatB2} на B2`]),
    en: () => c("Official Polish exam papers: where to download", "The Commission's papers from the 2021–2024 sittings", [`${N.certyfikatB1} at B1`, `${N.certyfikatB2} at B2`]),
  },
  pisanie: {
    preview: { kind: "writing" },
    pl: () => c("Pisanie na egzaminie z polskiego B1·B2", "Jeden list na telc, dwa teksty na państwowym", ["cztery kryteria oceny"]),
    uk: () => c("Письмо на іспиті з польської B1·B2", "Один лист на telc, два тексти на державному", ["чотири критерії оцінки"]),
    ru: () => c("Письмо на экзамене по польскому B1·B2", "Одно письмо на telc, два текста на государственном", ["четыре критерия оценки"]),
    en: () => c("Writing in the Polish exam, B1·B2", "One letter on telc, two texts on the state exam", ["four marking criteria"]),
  },
  slowka: {
    preview: { kind: "words" },
    pl: () => c("Polskie słowa według tematu", `${WORD_SETS.length} gotowych zestawów, część z wideo`, [`${WORD_SETS.length} tematów`, "po 10 słów"]),
    uk: () => c("Польські слова за темами", `${WORD_SETS.length} готових добірок, частина з відео`, [`${WORD_SETS.length} тем`, "по 10 слів"]),
    ru: () => c("Польские слова по темам", `${WORD_SETS.length} готовых подборок, часть с видео`, [`${WORD_SETS.length} тем`, "по 10 слов"]),
    en: () => c("Polish words by topic", `${WORD_SETS.length} ready-made sets, some with video`, [`${WORD_SETS.length} topics`, "10 words each"]),
  },
  "dla-szkol": {
    preview: { kind: "none" },
    pl: () => c("Symulator egzaminu z polskiego dla szkół", "Dodaj do kursu, do dwóch miesięcy za darmo", ["okres próbny bez opłat", "telc i egzamin państwowy"]),
    uk: () => c("Симулятор іспиту з польської для шкіл", "Додайте до курсу, до двох місяців безкоштовно", ["пробний період без оплати", "telc і державний іспит"]),
    ru: () => c("Симулятор экзамена по польскому для школ", "Добавьте к курсу, до двух месяцев бесплатно", ["пробный период без оплаты", "telc и государственный экзамен"]),
    en: () => c("Polish exam simulator for schools", "Add it to your course, up to two months free", ["free trial", "telc and the state exam"]),
  },
  "dla-korepetytorow": {
    preview: { kind: "none" },
    pl: () => c("Symulator egzaminu z polskiego dla korepetytorów", "Dostęp na 90 dni i kod dla twoich uczniów", ["telc i egzamin państwowy", "kod −25%"]),
    uk: () => c("Симулятор іспиту з польської для репетиторів", "Доступ на 90 днів і код для твоїх учнів", ["telc і державний іспит", "код −25%"]),
    ru: () => c("Симулятор экзамена по польскому для репетиторов", "Доступ на 90 дней и код для твоих учеников", ["telc и государственный экзамен", "код −25%"]),
    en: () => c("Polish exam simulator for tutors", "90 days of access and a code for your students", ["telc and the state exam", "code −25%"]),
  },
  terms: {
    preview: { kind: "none" },
    pl: () => c("Regulamin", "polskiegzamin — egzamin z polskiego B1·B2"),
    uk: () => c("Умови користування", "polskiegzamin — іспит з польської B1·B2"),
    ru: () => c("Условия использования", "polskiegzamin — экзамен по польскому B1·B2"),
    en: () => c("Terms of use", "polskiegzamin — the Polish exam, B1·B2"),
  },
  privacy: {
    preview: { kind: "none" },
    pl: () => c("Polityka prywatności", "polskiegzamin — egzamin z polskiego B1·B2"),
    uk: () => c("Політика конфіденційності", "polskiegzamin — іспит з польської B1·B2"),
    ru: () => c("Политика конфиденциальности", "polskiegzamin — экзамен по польскому B1·B2"),
    en: () => c("Privacy policy", "polskiegzamin — the Polish exam, B1·B2"),
  },
  cookies: {
    preview: { kind: "none" },
    pl: () => c("Polityka cookies", "polskiegzamin — egzamin z polskiego B1·B2"),
    uk: () => c("Політика cookies", "polskiegzamin — іспит з польської B1·B2"),
    ru: () => c("Политика cookie", "polskiegzamin — экзамен по польскому B1·B2"),
    en: () => c("Cookie policy", "polskiegzamin — the Polish exam, B1·B2"),
  },
  kontakt: {
    preview: { kind: "none" },
    pl: () => c("Kontakt", "polskiegzamin — egzamin z polskiego B1·B2"),
    uk: () => c("Контакт", "polskiegzamin — іспит з польської B1·B2"),
    ru: () => c("Контакты", "polskiegzamin — экзамен по польскому B1·B2"),
    en: () => c("Contact", "polskiegzamin — the Polish exam, B1·B2"),
  },
  poradnik: {
    preview: { kind: "papers" },
    pl: () => c("Poradnik: egzamin z polskiego B1 i B2", "Konkretne pytania o telc i egzamin państwowy", ["liczby z arkuszy", "ze źródłami"]),
    uk: () => c("Порадник: іспит з польської B1 і B2", "Конкретні питання про telc і державний іспит", ["цифри з аркушів", "з джерелами"]),
    ru: () => c("Блог: экзамен по польскому B1 и B2", "Конкретные вопросы о telc и государственном экзамене", ["цифры из наборов", "с источниками"]),
    en: () => c("Guides: the Polish exam at B1 and B2", "Specific questions about telc and the state exam", ["figures from the papers", "with sources"]),
  },
  // One card per post, written beside the post in `features/blog/catalogue.ts`.
  ...Object.fromEntries(
    POSTS.map((p): [string, Entry] => [
      `poradnik-${p.slug}`,
      {
        preview: p.preview,
        pl: () => p.card.pl,
        uk: () => p.card.uk,
        ru: () => p.card.ru,
        en: () => p.card.en,
      },
    ]),
  ),
};

/** `/telc/zasady` → `telc-zasady`; the home page is `home`. */
export function ogSlug(route: string): string {
  return route === "/" ? "home" : route.replace(/^\//, "").replace(/\//g, "-");
}

export const OG_SLUGS: string[] = Object.keys(ENTRIES);

export function ogCardForSlug(slug: string, locale: OgLocale): OgCard | undefined {
  const entry = ENTRIES[slug];
  return entry && { ...entry[locale](), preview: entry.preview };
}

export const ogCard = (route: string, locale: OgLocale) => ogCardForSlug(ogSlug(route), locale);

/** The path under the site's origin, without the leading origin. */
export function ogImagePath(route: string, locale: OgLocale): string | undefined {
  const slug = ogSlug(route);
  return slug in ENTRIES ? `/og/${OG_VERSION}/${locale}/${slug}.png` : undefined;
}

/** What the picture shows, in words, for `og:image:alt`. */
export function ogAlt(card: OgCard): string {
  const stop = (x: string) => (/[.?!]$/.test(x) ? x : `${x}.`);
  return [stop(card.title), stop(card.sub), card.chips.join(" · ")].filter(Boolean).join(" ");
}
